/**
 * NUMERAÇÃO ZR DA RECOMMENDATION.
 *
 * O ZR é um atributo da Recommendation, não a sua identidade: editar ou
 * remover o número mexe apenas nesse campo. A Recommendation continua
 * existindo com o mesmo id, e sem número ela volta para a fila de pendência
 * de numeração.
 *
 * Não há padrão de formato inventado aqui. O sistema sempre gravou o ZR como
 * texto livre digitado pelo engenheiro (ver o campo "ZR" do cadastro de
 * Recommendation); o que se valida é o que é regra de verdade: não vazio, sem
 * espaços sobrando e sem colidir com outra Recommendation.
 */

import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export type ZrHistoryAction = "ASSIGNED" | "UPDATED" | "REMOVED";

export interface ZrHistoryEntry {
  action: ZrHistoryAction;
  /** Número antes da ação (null quando a Recommendation estava pendente). */
  previousZr: string | null;
  /** Número depois da ação (null quando a numeração foi removida). */
  newZr: string | null;
  reason: string;
  performedAt: string;
  performedByUserId: string | null;
  performedByProfessionalId: string | null;
}

/** Rótulo do estado sem numeração — o "status" de pendência é o ZR nulo. */
export const ZR_PENDING_LABEL = "PENDENTE DE NUMERAÇÃO";

export const ZR_REQUIRED_MESSAGE = "Informe o número ZR.";
export const ZR_REASON_REQUIRED_MESSAGE = "Informe o motivo da alteração da numeração ZR.";
export const ZR_REMOVAL_REASON_REQUIRED_MESSAGE = "Informe o motivo da remoção da numeração ZR.";
export const ZR_ALREADY_PENDING_MESSAGE = "Esta recomendação já está pendente de numeração ZR.";

export function zrDuplicateMessage(zr: string): string {
  return `O número ${zr} já está associado a outra recomendação.`;
}

/** Normaliza o que o usuário digitou: sem sobras nas pontas nem espaços duplos. */
export function normalizeZrNumber(value: string | null | undefined): string {
  return String(value ?? "").trim().replace(/\s+/g, " ");
}

/** Recommendation sem número está pendente de numeração. */
export function isPendingZr(recommendation: { zr?: string | null } | null | undefined): boolean {
  return normalizeZrNumber(recommendation?.zr) === "";
}

/** Texto a exibir no lugar do número quando não há numeração. */
export function formatZrDisplay(recommendation: { zr?: string | null } | null | undefined): string {
  return isPendingZr(recommendation) ? ZR_PENDING_LABEL : normalizeZrNumber(recommendation?.zr);
}

export function requireZrNumber(value: string | null | undefined): string {
  const normalized = normalizeZrNumber(value);
  if (!normalized) {
    throw new AppError(ErrorCode.Validation, "ZR number is required", { safeMessage: ZR_REQUIRED_MESSAGE });
  }
  return normalized;
}

export function requireZrReason(value: string | null | undefined, message = ZR_REASON_REQUIRED_MESSAGE): string {
  const reason = String(value ?? "").trim();
  if (!reason) {
    throw new AppError(ErrorCode.Validation, "ZR change reason is required", { safeMessage: message });
  }
  return reason;
}

/**
 * Dois números iguais em recomendações diferentes quebram a rastreabilidade do
 * documento. A comparação ignora caixa e espaços, que é como um humano leria.
 */
export function assertZrNotDuplicated(
  zr: string,
  others: readonly { id: string; zr?: string | null }[],
  currentRecommendationId: string
): void {
  const wanted = normalizeZrNumber(zr).toUpperCase();
  const clash = others.find(
    other => other.id !== currentRecommendationId && normalizeZrNumber(other.zr).toUpperCase() === wanted
  );

  if (clash) {
    throw new AppError(ErrorCode.Conflict, `ZR ${zr} already used by recommendation ${clash.id}`, {
      safeMessage: zrDuplicateMessage(normalizeZrNumber(zr))
    });
  }
}

/**
 * Acrescenta um evento ao histórico da numeração. O histórico é append-only:
 * um número removido continua registrado, mesmo o campo atual ficando vazio.
 */
export function appendZrHistory(
  history: readonly ZrHistoryEntry[] | undefined,
  entry: ZrHistoryEntry
): ZrHistoryEntry[] {
  return [...(history ?? []), entry];
}

/** Números que já pertenceram a esta Recommendation, do mais antigo ao mais recente. */
export function listHistoricZrNumbers(history: readonly ZrHistoryEntry[] | undefined): string[] {
  const numbers: string[] = [];
  for (const entry of history ?? []) {
    for (const value of [entry.previousZr, entry.newZr]) {
      const normalized = normalizeZrNumber(value);
      if (normalized && !numbers.includes(normalized)) numbers.push(normalized);
    }
  }
  return numbers;
}
